/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Icon, BoxSpinnerView } from '@components/atoms'
import { List, ListItemButton } from '@components/molecules'
import { AccessGroupListItem } from '@components/systems/accessControl/AccessGroupListItem'
import { mono, boFont } from '@components/systems/backoffice/backoffice.tokens'

import { useAppSelector, CrudType } from '@redux/index'
import { AccessGroupType } from '@schemas'

import { ApiGetDataStatus } from '@redux/index'

interface PropsType {
	groupList: AccessGroupType[]
	status: ApiGetDataStatus
	onGroupClick: (aGroup: AccessGroupType) => void
	onAddButtonClick?: () => void
	selectedGroupIds?: string[]
	showAddButton?: boolean
	rightAccessory?: (aGroup: AccessGroupType, isChecked: boolean) => JSX.Element
}

/** 그룹 목록 — 헤어라인 행. 선택 = ink 글자 + 옅은 잉크 면. 추가는 마지막 행의 + */
export const AccessGroupList = (props: PropsType) => {
	const { groupList, status, onAddButtonClick } = props

	// redux state
	const crud: CrudType = useAppSelector(state => state.auth.currentAccess)

	// variables
	const showAddButton = props.showAddButton ?? false

	const onGroupClick = (aGroup: AccessGroupType) => {
		props.onGroupClick(aGroup)
	}

	const RenderAddButton = ({ style }: { style?: React.CSSProperties }) => {
		return (
			<>
				{crud.includes('C') && (
					<ListItemButton
						className="listItem addItem"
						css={{ ...style }}
						onClick={onAddButtonClick}
					>
						<View className="addView">
							<Icon name="plus" className="addIcon" />
							<span className="addLabel">그룹 추가</span>
						</View>
					</ListItemButton>
				)}
			</>
		)
	}

	const RightAccessory = ({
		group,
		isChecked,
	}: {
		group: AccessGroupType
		isChecked: boolean | undefined
	}) => {
		return (
			<>{props.rightAccessory ? props.rightAccessory(group, isChecked ?? false) : null}</>
		)
	}

	return (
		<View css={style}>
			{(status === 'init' || status === 'loading') && (
				<View className="spinnerView">
					<BoxSpinnerView />
				</View>
			)}

			<View>
				<View className="listOuter">
					<List>
						{groupList.map(aGroup => {
							const isChecked: boolean | undefined = props.selectedGroupIds
								? props.selectedGroupIds.includes(aGroup.id)
								: undefined

							return (
								<View key={aGroup.id} className="listItemView">
									<ListItemButton
										className={`listItem ${isChecked ? 'checked' : ''}`}
										onClick={() => onGroupClick(aGroup)}
									>
										<AccessGroupListItem group={aGroup} />
									</ListItemButton>

									{props.rightAccessory && (
										<View className={`rightAccessory ${isChecked ? 'checked' : ''}`}>
											<RightAccessory group={aGroup} isChecked={isChecked} />
										</View>
									)}
								</View>
							)
						})}

						<View className="listItemView">{showAddButton && <RenderAddButton />}</View>
					</List>
				</View>
			</View>
		</View>
	)
}

const style = css({
	position: 'relative',
	width: '100%',
	height: '100%',
	fontFamily: boFont.ui,

	'.spinnerView': {
		position: 'absolute',
		zIndex: 9,
		width: '100%',
		height: '100%',
		top: 0,
		background: mono.bg,
		opacity: 0.6,
	},

	'.listOuter': {
		padding: 0,

		'.MuiList-root': { padding: 0 },

		'.listItemView': {
			position: 'relative',

			'.rightAccessory': {
				position: 'absolute',
				zIndex: 5,
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				top: 0,
				bottom: 0,
				right: 0,

				'.listItemEdit': {
					position: 'absolute',
					height: '2rem',
					right: '0.5rem',
					top: 0,
					bottom: 0,
					margin: 'auto',
					opacity: 0,
					transition: 'opacity 200ms ease-out',
				},

				'&.checked': {
					'.listItemEdit': {
						opacity: 1,
					},
				},
			},

			'&:hover': {
				'.listItemEdit': {
					opacity: 1,
				},
			},

			'.listItem': {
				minHeight: 44,
				padding: '0 12px',
				marginBottom: 0,
				background: 'transparent',
				borderRadius: 0,
				borderBottom: `1px solid ${mono.line}`,
				color: mono.dim,
				transition: 'color 200ms ease-out, background-color 200ms ease-out',

				'&:hover': { color: mono.ink, background: 'rgba(22, 24, 27, 0.03)' },

				'&.checked': {
					background: 'rgba(22, 24, 27, 0.05)',
					color: mono.ink,
					boxShadow: `inset 1px 0 0 ${mono.ink}`,
				},

				'&.addItem': {
					display: 'flex',
					justifyContent: 'flex-start',
					alignItems: 'center',
					borderBottom: `1px solid ${mono.line}`,

					'.addView': {
						display: 'flex',
						alignItems: 'center',
						gap: 8,

						'.addIcon': { fontSize: 16, color: mono.faint },
						'.addLabel': { fontSize: 12, fontWeight: 500, color: mono.faint },
					},

					'&:hover .addIcon, &:hover .addLabel': { color: mono.ink },
				},
			},
		},
	},
})
